import * as React from 'react';
import {HStack, Text, View, VStack} from '@packages/systems/spring';
import {StylePanelLabel} from '@packages/systems/ui/shared-ui/StylePanelLabel';
import PropertyList, {
  PropertyListItem,
} from '@packages/systems/ui/shared-ui/PropertyList';
import {SCHEMA_ICON_MAP} from './SchemaIconMap';
import {collectFieldsInsideForm} from '@packages/utilities/platform';
import type {ExpressionElement} from '@packages/systems/wfdl-ast';

type FormField = {
  fieldName: string;
  fieldType: {
    tag: keyof typeof SCHEMA_ICON_MAP;
  };
};

type FormFieldItem = {
  id: string;
  label: string;
  icon: React.ReactNode;
  fieldType: string;
};

const transformFormFieldsToItems = (
  formFields: FormField[]
): FormFieldItem[] => {
  return formFields.map(({fieldName, fieldType}) => {
    const FieldIcon = SCHEMA_ICON_MAP[fieldType.tag];
    return {
      id: fieldName,
      label: fieldName,
      icon: FieldIcon ? (
        <FieldIcon data-automation-id="form-field-icon" />
      ) : null,
      fieldType: fieldType.tag,
    };
  });
};

const FormFieldRow = ({item}: {item: FormFieldItem}) => {
  return (
    <PropertyListItem
      id={item.id}
      label={
        <HStack justify="space-between" align="center">
          <Text>{item.label}</Text>
          <Text colorVariant="secondary">{item.fieldType}</Text>
        </HStack>
      }
      icon={item.icon}
      data-automation-id="form-field-row"
    />
  );
};

const EmptyFieldsState = () => (
  <View border="$border1" borderRadius="$2" padding="$2" textAlign="center">
    No form fields added
  </View>
);

export type SchemaSectionProps = {
  formFormElement: ExpressionElement;
};
export const SchemaSection = ({formFormElement}: SchemaSectionProps) => {
  const formFieldObj = collectFieldsInsideForm(formFormElement, {
    password: true,
    recaptcha: true,
  });
  const formFields = formFieldObj?.fields || [];
  const fieldItems = transformFormFieldsToItems(formFields);

  return (
    <div>
      <VStack>
        <StylePanelLabel>
          <Text>Fields</Text>
        </StylePanelLabel>
        <PropertyList
          items={fieldItems}
          renderItem={(item) => <FormFieldRow key={item.id} item={item} />}
          renderNone={() => <EmptyFieldsState />}
          deletable={false}
          duplicable={false}
          sortable={false}
          useLargeIcons
        />
      </VStack>
    </div>
  );
};
